import type { PublicPlanCard, PublicPlanMetric } from "@/api/queries/publicPlans";

/**
 * "From which plan onwards do I get this?" — answered from the plan
 * catalogue rather than from copy.
 *
 * The security page makes claims like "single sign-on from Business up".
 * Written into a sentence, that claim survives the repricing that
 * invalidates it: an admin moves an entitlement between tiers in
 * `/admin/plans`, the page keeps saying the old tier name, and nothing
 * fails. Derived from ``GET /public/plans`` it cannot drift.
 */

export type Availability =
  /** Every published plan grants it, including the cheapest. */
  | { kind: "all" }
  /**
   * Granted from this plan up. The whole card comes back, not a
   * name: the name is bilingual (`name` / `name_en`) and only the
   * caller knows which locale it is rendering.
   */
  | { kind: "from"; plan: PublicPlanCard }
  /** No published plan grants it — the honest answer is "ask us". */
  | { kind: "none" };

/**
 * Ranked by the catalogue's own ``sort_order`` — the same field the
 * pricing page ranks cards by, so the two pages cannot disagree about
 * which tier comes first.
 *
 * Sorted here rather than trusted from the array: the payload arrives
 * ordered today, but "the cheapest plan that grants this" is the whole
 * claim, and resting it on an ordering nobody restates is how a page
 * ends up confidently naming the wrong tier.
 */
export function entitlementAvailability(
  plans: readonly PublicPlanCard[],
  key: string,
): Availability {
  if (!plans.length) return { kind: "none" };
  const granting = plans
    .filter((plan) => plan.default_entitlements.includes(key))
    .sort((a, b) => a.sort_order - b.sort_order);
  if (!granting.length) return { kind: "none" };
  if (granting.length === plans.length) return { kind: "all" };
  return { kind: "from", plan: granting[0]! };
}

/**
 * The same question for a plan LIMIT rather than a switch — "from which
 * plan may I have any of these at all?" (KAN-1949, audit #55).
 *
 * Some capabilities are not an entitlement key but a count: API tokens
 * for reading data are capped by ``max_api_tokens`` (``0`` on a tier
 * that gets none), and only *writing* is the ``api.write`` key. Badging
 * the read card with ``api.write`` named Business on the features page
 * while the pricing table — reading the very same limit — showed tokens
 * from Pro.
 *
 * Read from the ``metrics`` rows of ``GET /public/plans``, the rows the
 * pricing table renders, so the two pages answer from one source. A
 * ``null`` value is "unlimited" there and counts as granted; a metric the
 * catalogue does not publish answers ``none`` (no badge, no claim).
 */
export function limitAvailability(
  plans: readonly PublicPlanCard[],
  metrics: readonly PublicPlanMetric[],
  key: string,
): Availability {
  const metric = metrics.find((row) => row.key === key);
  if (!plans.length || !metric) return { kind: "none" };
  const granting = plans
    .filter((plan) => {
      if (!(plan.tier in metric.values)) return false;
      const value = metric.values[plan.tier];
      return value === null || (value ?? 0) > 0;
    })
    .sort((a, b) => a.sort_order - b.sort_order);
  if (!granting.length) return { kind: "none" };
  if (granting.length === plans.length) return { kind: "all" };
  return { kind: "from", plan: granting[0]! };
}
